import { createFileRoute } from '@tanstack/react-router';
import { useSesion } from '../../features/auth/api/useSesion';
import { Bienvenida } from '../../features/bienvenida/components/bienvenida';

/** Número fijo de pasos del recorrido (documento diseño 2026-09-02, capturas
 * `bienvenida-{1..4}-movil.png`): las cuatro tarjetas que `puerta:bienvenida` declara
 * (`paso1`..`paso4`) y ninguna más — no es un dato de servidor, así que vive como constante de
 * esta ruta, la única que lo necesita. */
const TOTAL_PASOS = 4;

interface BusquedaBienvenida {
	/** Paso actual del recorrido, 1..`TOTAL_PASOS`. Por defecto 1; cualquier valor fuera de rango
	 * (incluido lo que no sea un número) se acota al hueco válido más cercano, nunca se descarta
	 * con un error — mismo criterio laxo que `leerBusquedaLogin`/`leerBusquedaCrearExperiencia`:
	 * es una lectura de `location.search`, no un contrato con el backend. */
	paso: number;
}

const acotarPaso = (valor: unknown): number => {
	const numero = typeof valor === 'number' ? valor : Number(valor);
	if (!Number.isFinite(numero)) {
		return 1;
	}
	return Math.min(TOTAL_PASOS, Math.max(1, Math.trunc(numero)));
};

const leerBusquedaBienvenida = (busqueda: Record<string, unknown>): BusquedaBienvenida => ({
	paso: acotarPaso(busqueda.paso),
});

/**
 * `/bienvenida` (T-3.8, oleada 3; realineada con el mock en la tarea de realineación de
 * `/bienvenida`): los cuatro pasos que reciben a quien ya tiene cuenta, contados por Tuad. La
 * guarda de sesión la da el layout `_autenticado` (su propio `beforeLoad` ya garantiza `GET /me`
 * resuelto antes de llegar aquí) — esta ruta no repite ningún guard propio, solo lee `?paso=` de
 * la URL y reparte `paso`/`total` a `Bienvenida`, el componente presentacional que no conoce
 * rutas.
 *
 * `staticData: { anchoCompleto: true }` (mismo mecanismo que ya usa `routes/login.tsx`, ver su
 * JSDoc y el de `routes/__root.tsx`): la pantalla se pinta a sangre, sin el contenedor de lectura
 * `mx-auto max-w-3xl px-4 py-8 sm:px-6` que `__root.tsx` aplica por defecto a `<main>` — el mock
 * (`.bien`) ocupa toda la ventana con la tarjeta centrada encima, no una columna de lectura.
 * `rutaDeAcceso` NO se marca: a diferencia de `/login`, aquí SÍ hay sesión, así que la cabecera y
 * el aviso de modo visitante se siguen pintando con normalidad.
 *
 * `nombre` (T-3.8, decisión propia — ver el informe final de la tarea): `puerta:bienvenida.
 * paso1.titulo` interpola `{{nombre}}` ("Estás dentro, {{nombre}}") y no existe ninguna variante
 * sin nombre en el fichero congelado (a diferencia de `shell:inicio.saludo`/`saludoSinNombre`,
 * que sí separan los dos casos) — se lee `sesion.fullName` con el mismo hook que ya usa
 * `routes/index.tsx` para `Inicio` y se pasa tal cual; sin sesión resuelta todavía (no debería
 * pasar bajo el guard de `_autenticado.tsx`, pero `useSesion` puede devolver `undefined` en el
 * primer render mientras la cache se asienta) cae a cadena vacía, el valor más cercano a la
 * clave existente sin inventar ninguna nueva.
 */
export const Route = createFileRoute('/_autenticado/bienvenida')({
	component: PantallaBienvenida,
	staticData: { anchoCompleto: true },
	validateSearch: leerBusquedaBienvenida,
});

/**
 * El envoltorio que devuelve esta función es el hijo DIRECTO de `<main>` en la rama
 * `anchoCompleto` de `__root.tsx` (`grid grid-rows-1 flex-1`, con `<Outlet />` a secas dentro, sin
 * ningún contenedor de lectura). Por eso este `<div>` NO lleva `mx-auto`: es la trampa que
 * `__root.tsx` documenta largo y tendido (líneas ~90-116, ya pagada tres veces en este
 * repositorio) — un hijo directo de esa rejilla con márgenes automáticos en el eje transversal
 * deja de estirarse y se encoge a su contenido, así que la rejilla nunca llegaría a ocupar el
 * ancho/alto completos que `place-items-center` necesita para centrar de verdad. En su lugar,
 * este `<div>` es él mismo una rejilla de una pista (`grid min-h-full place-items-center`, con
 * relleno `px-4 py-8`) que se estira sin problema como hijo del `<main>` exterior, y es DENTRO de
 * ella donde `Bienvenida` centra su propia `Card` con su propio `mx-auto` — ese `mx-auto` interior
 * no reproduce la trampa porque ya no es un hijo directo de ninguna rejilla que necesite estirarse
 * a su alrededor.
 */
function PantallaBienvenida(): React.JSX.Element {
	const { paso } = Route.useSearch();
	const { sesion } = useSesion();

	return (
		<div className='grid min-h-full place-items-center px-4 py-8'>
			<Bienvenida nombre={sesion?.fullName ?? ''} paso={paso} total={TOTAL_PASOS} />
		</div>
	);
}
